import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { Home, Calendar, Clock, DollarSign, FileText, BarChart, CheckCircle, AlertTriangle, Gavel, FileSpreadsheet, CloudRain, ArrowRightLeft, Leaf, Building, BadgeDollarSign, MapPin, TreePine, X, Award, User, TrendingUp, CircleCheck, Eye } from 'lucide-react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";

interface Pregao {
  id: string;
  titulo: string;
  dataInicio: string;
  dataFim: string;
  status: 'agendado' | 'em_andamento' | 'finalizado' | 'cancelado';
  valorEstimado: number;
  descricao: string;
  categoria: string;
}

// Interfaces para as novas seções
interface ProjetoOfertante {
  id: string;
  nome: string;
  tipo: string;
  regiao: string;
  creditosGerados: number;
  statusVerificacao: 'Verificado' | 'Em análise' | 'Pendente';
}

interface EmpresaCompradora {
  id: string;
  nome: string;
  setor: string;
  creditosAdquiridos: number;
  ultimaCompra: string;
}

const PregaoDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [pregaoSelecionado, setPregaoSelecionado] = useState<Pregao | null>(null);
  const [modalResultadosAberto, setModalResultadosAberto] = useState<boolean>(false);
  const [modalLoginAberto, setModalLoginAberto] = useState<boolean>(false);
  const [credenciais, setCredenciais] = useState({ usuario: '', senha: '' });
  const [erroLogin, setErroLogin] = useState<string | null>(null);
  const [projetosDialogOpen, setProjetosDialogOpen] = useState(false);
  
  // Mock de dados para pregões
  const pregoes: Pregao[] = [
    {
      id: 'PG-2025-002',
      titulo: 'Créditos de Carbono - Energia Solar',
      dataInicio: '2025-05-10T10:00:00',
      dataFim: '2025-05-10T16:00:00',
      status: 'em_andamento',
      valorEstimado: 850000,
      descricao: 'Aquisição de 15.000 créditos de carbono certificados provenientes de projetos de energia solar no Nordeste brasileiro.',
      categoria: 'Energia Renovável'
    },
    {
      id: 'PG-2025-003',
      titulo: 'Créditos de Carbono - Restauração de Manguezais',
      dataInicio: '2025-04-05T08:00:00',
      dataFim: '2025-04-05T14:00:00',
      status: 'finalizado',
      valorEstimado: 650000,
      descricao: 'Aquisição de 12.000 créditos de carbono provenientes de projetos de restauração de manguezais na costa brasileira.',
      categoria: 'Costeiro'
    },
    {
      id: 'PG-2025-004',
      titulo: 'Créditos de Carbono - Biogás Rural',
      dataInicio: '2025-04-20T09:30:00',
      dataFim: '2025-04-20T15:30:00',
      status: 'cancelado',
      valorEstimado: 430000,
      descricao: 'Aquisição de 8.000 créditos de carbono de projetos de produção de biogás a partir de resíduos agrícolas.',
      categoria: 'Agropecuário'
    }
  ];
  
  // Mock de dados para projetos ofertantes
  const projetosOfertantes: ProjetoOfertante[] = [
    {
      id: 'PRJ-2023-001',
      nome: 'Reflorestamento Vale do Ribeira',
      tipo: 'Florestal',
      regiao: 'Sudeste',
      creditosGerados: 15000,
      statusVerificacao: 'Verificado'
    },
    {
      id: 'PRJ-2023-002',
      nome: 'Energia Solar Sertão Nordestino',
      tipo: 'Energia Renovável',
      regiao: 'Nordeste',
      creditosGerados: 8500,
      statusVerificacao: 'Verificado'
    },
    {
      id: 'PRJ-2023-003',
      nome: 'Preservação Manguezais Costeiros',
      tipo: 'Costeiro',
      regiao: 'Sul',
      creditosGerados: 12000,
      statusVerificacao: 'Em análise'
    }
  ];
  
  // Mock de dados para empresas compradoras
  const empresasCompradoras: EmpresaCompradora[] = [
    {
      id: 'EMP-2023-001',
      nome: 'Natura Cosméticos',
      setor: 'Cosméticos',
      creditosAdquiridos: 8000,
      ultimaCompra: '2023-10-15'
    },
    {
      id: 'EMP-2023-002',
      nome: 'Banco Itaú',
      setor: 'Financeiro',
      creditosAdquiridos: 12500,
      ultimaCompra: '2023-11-22'
    },
    {
      id: 'EMP-2023-003',
      nome: 'Ambev',
      setor: 'Bebidas',
      creditosAdquiridos: 6800,
      ultimaCompra: '2023-09-05'
    },
    {
      id: 'EMP-2023-004',
      nome: 'Magazine Luiza',
      setor: 'Varejo',
      creditosAdquiridos: 5200,
      ultimaCompra: '2023-12-08'
    }
  ];
  
  // Objeto para armazenar dados dos resultados de cada pregão
  const resultadosPregoes: Record<string, {
    vencedor: string;
    valorFinal: number;
    quantidadeCreditos: number;
    dataFinalizacao: string;
    participantes: {
      empresa: string;
      lance: number;
      posicao: number;
      status: 'vencedor' | 'desclassificado' | 'participante';
    }[];
    detalhes: string;
  }> = {
    'PG-2025-003': {
      vencedor: 'EcoVerde Energia',
      valorFinal: 680000,
      quantidadeCreditos: 12000,
      dataFinalizacao: '2025-04-05T14:00:00',
      participantes: [
        { 
          empresa: 'EcoVerde Energia', 
          lance: 680000, 
          posicao: 1, 
          status: 'vencedor' 
        },
        { 
          empresa: 'BrasilCarbon', 
          lance: 660000, 
          posicao: 2, 
          status: 'participante' 
        },
        { 
          empresa: 'Natura Sustentável', 
          lance: 655000, 
          posicao: 3, 
          status: 'participante' 
        },
        { 
          empresa: 'GreenTech Solutions', 
          lance: 645000, 
          posicao: 4, 
          status: 'participante' 
        },
        { 
          empresa: 'AgroForest', 
          lance: 630000, 
          posicao: 5, 
          status: 'desclassificado' 
        }
      ],
      detalhes: 'O pregão foi finalizado com sucesso, com participação de 5 empresas. O valor final foi 4,6% acima do valor estimado inicial.'
    },
    'PG-2025-004': {
      vencedor: 'N/A',
      valorFinal: 0,
      quantidadeCreditos: 8000,
      dataFinalizacao: '2025-04-20T15:30:00',
      participantes: [
        { 
          empresa: 'BioEnergy Corp', 
          lance: 420000, 
          posicao: 1, 
          status: 'desclassificado' 
        },
        { 
          empresa: 'Agrosul', 
          lance: 410000, 
          posicao: 2, 
          status: 'desclassificado' 
        }
      ],
      detalhes: 'O pregão foi cancelado devido ao não atingimento do valor mínimo estipulado para os créditos. Um novo pregão será agendado.'
    }
  };
  
  // Função para formatar data
  const formatarData = (dataString: string): string => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // Função para formatar hora
  const formatarHora = (dataString: string): string => {
    const data = new Date(dataString);
    return data.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Função para formatar valor
  const formatarValor = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };
  
  // Obter ícone e cor com base no status
  const obterStatusInfo = (status: Pregao['status']) => {
    switch (status) {
      case 'agendado':
        return { 
          texto: 'Agendado', 
          icone: <Calendar className="w-4 h-4 mr-1" />, 
          cor: 'bg-blue-100 text-blue-800'
        };
      case 'em_andamento':
        return { 
          texto: 'Em Andamento', 
          icone: <Clock className="w-4 h-4 mr-1" />, 
          cor: 'bg-amber-100 text-amber-800'
        };
      case 'finalizado':
        return { 
          texto: 'Finalizado', 
          icone: <CheckCircle className="w-4 h-4 mr-1" />, 
          cor: 'bg-green-100 text-green-800'
        };
      case 'cancelado':
        return { 
          texto: 'Cancelado', 
          icone: <AlertTriangle className="w-4 h-4 mr-1" />, 
          cor: 'bg-red-100 text-red-800'
        };
      default:
        return { 
          texto: 'Desconhecido', 
          icone: null, 
          cor: 'bg-gray-100 text-gray-800'
        };
    }
  };
  
  // Filtrar pregões por status
  const pregoesAgendados = pregoes.filter(pregao => pregao.status === 'agendado');
  const pregoesEmAndamento = pregoes.filter(pregao => pregao.status === 'em_andamento');
  const pregoesFechados = pregoes.filter(pregao => pregao.status === 'finalizado' || pregao.status === 'cancelado');
  
  // Navegar para página do pregão
  const navegarParaPregao = (id: string) => {
    navigate(`/pregao-detalhe/${id}`);
  };
  
  // Navegar para página de lances do pregão
  const navegarParaLances = (id: string) => {
    navigate(`/pregao-lances/${id}`);
  };
  
  // Navegar para página de detalhes do projeto ofertante
  const navegarParaProjetoOfertante = (id: string) => {
    navigate(`/projeto-ofertante/${id}`);
  };
  
  // Abrir modal com resultados do pregão
  const abrirResultadosPregao = (pregao: Pregao) => {
    setPregaoSelecionado(pregao);
    setModalResultadosAberto(true);
  };
  
  // Função para abrir o modal de login do proprietário
  const abrirModalLogin = () => {
    setModalLoginAberto(true);
    setErroLogin(null);
    setCredenciais({ usuario: '', senha: '' });
  };

  // Função para fechar o modal de login
  const fecharModalLogin = () => {
    setModalLoginAberto(false);
  };

  // Função para realizar login e navegar para a área do proprietário
  const realizarLogin = () => {
    // Para fins de teste, aceita qualquer email e senha
    // Redireciona para a área do proprietário
    fecharModalLogin();
    navigate('/area-proprietario');
    
    // Comentado para permitir qualquer login durante testes
    // setErroLogin('Usuário ou senha inválidos. Tente novamente.');
  };

  // Função para lidar com a mudança nos campos de input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredenciais(prev => ({ ...prev, [name]: value }));
  };
  
  const isActivePath = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === 'projects') {
      return ['/my-projects', '/project/status', '/project/region', '/all-projects'].some(
        p => location.pathname.startsWith(p)
      );
    }
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleProjetosClick = () => {
    setProjetosDialogOpen(true);
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      {/* Menu horizontal superior */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex space-x-6 overflow-x-auto py-3">
          <button 
            className={`${isActivePath('/planos') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/planos')}
          >
            Planos
          </button>
          <button 
            className={`${isActivePath('/dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`${isActivePath('projects') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={handleProjetosClick}
          >
            Projetos
          </button>
          <button 
            className={`${isActivePath('/emission') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/emission')}
          >
            Emissão
          </button>
          <button 
            className={`${isActivePath('/traceability') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/traceability')}
          >
            Rastreabilidade
          </button>
          <button 
            className={`${isActivePath('/certificates') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/certificates')}
          >
            Certificados
          </button>
          <button
            className={`${isActivePath('/pregao-dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/pregao-dashboard')}
          >
            Bolsa de Crédito de Carbono Voluntário
          </button>
          <button 
            className={`${isActivePath('/compliance') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/compliance')}
          >
            Conformidade
          </button>
          <button 
            className={`${isActivePath('/retirement') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/retirement')}
          >
            Aposentadoria
          </button>
          <button 
            className={`${isActivePath('/educational') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/educational')}
          >
            Espaço Educativo
          </button>
        </div>
      </div>
      {/* Pop-up de Acesso para Projetos */}
      <Dialog open={projetosDialogOpen} onOpenChange={setProjetosDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Acesso a Projetos</DialogTitle>
            <DialogDescription className="text-center">
              Selecione o tipo de visualização de projetos que deseja acessar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/my-projects');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Meus Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Visualize todos os seus projetos cadastrados</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/status');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Status dos Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acompanhe o status de todos os projetos</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/region');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Projetos por Região</h3>
                <p className="text-sm text-gray-500 text-center">Explore projetos por localização geográfica</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              window.open('https://banclima.com/projetos', '_blank');
              setProjetosDialogOpen(false);
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Portal de Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acesse o portal público de projetos do Banclima</p>
              </CardContent>
            </Card>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button 
              variant="outline" 
              onClick={() => setProjetosDialogOpen(false)}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Título e botões de ação */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Dashboard de Pregão Eletrônico</h1>
            <p className="text-gray-600">Bem-vindo(a) ao sistema de pregão eletrônico de créditos de carbono</p>
          </div>
          <div className="flex space-x-2">
            <Button
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
              onClick={abrirModalLogin}
            >
              <User className="h-4 w-4 mr-2" />
              Acesso ao Proprietário
            </Button>
            <Button
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
              onClick={() => navigate('/cadastro-proponente')}
            >
              <FileText className="h-4 w-4 mr-2" />
              Cadastro de Proponente
            </Button>
            <Button 
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
              onClick={() => navigate('/dashboard')}
            >
              <Home className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Seção "Sobre o Pregão Eletrônico" */}
      <div className="bg-white px-6 py-6 border-b border-gray-200">
        <div className="container mx-auto">
          <div className="text-left">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Sobre o Pregão Eletrônico</h2>
            <div className="text-gray-700 space-y-3">
              <p>
                Este pregão eletrônico tem como origem a iniciativa de aquisição/negociação de créditos de carbono, promovida por [nome da instituição ou órgão responsável], com o objetivo de fomentar práticas sustentáveis e contribuir para a mitigação das mudanças climáticas.
              </p>
              <p>
                A contratação segue as diretrizes legais vigentes e está alinhada às políticas ambientais e climáticas, promovendo a redução efetiva das emissões de Gases de Efeito Estufa (GEE).
              </p>
              <p>
                Este processo assegura transparência, isonomia e competitividade, fortalecendo o compromisso com o desenvolvimento sustentável e o cumprimento de metas de neutralização de carbono.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Conteúdo principal */}
      <div className="flex-grow p-6 bg-gray-50">
        <div className="container mx-auto">
          {/* Cards de estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="p-6 flex items-center">
                <div className="rounded-full p-4 bg-white shadow-sm mr-4">
                  <Clock className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Em Andamento</p>
                  <h3 className="text-2xl font-bold">{pregoesEmAndamento.length}</h3>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 flex items-center">
                <div className="rounded-full p-4 bg-white shadow-sm mr-4">
                  <CheckCircle className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Finalizados</p>
                  <h3 className="text-2xl font-bold">{pregoesFechados.filter(p => p.status === 'finalizado').length}</h3>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 flex items-center">
                <div className="rounded-full p-4 bg-white shadow-sm mr-4">
                  <FileText className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Minhas Propostas</p>
                  <h3 className="text-2xl font-bold">3</h3>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Pregões disponíveis */}
          <div className="mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {pregoesAgendados.map((pregao) => (
                <Card key={pregao.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center mb-2">
                          {obterStatusInfo(pregao.status).icone}
                          <span className={`ml-2 text-sm px-2 py-0.5 rounded ${obterStatusInfo(pregao.status).cor}`}>
                            {obterStatusInfo(pregao.status).texto}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold mb-1">{pregao.titulo}</h3>
                        <p className="text-sm text-gray-500 mb-4 line-clamp-2">{pregao.descricao}</p>
                      </div>
                      <div className="ml-4 flex flex-col items-end">
                        <span className="text-sm font-semibold text-[#0e7cb4]">{pregao.id}</span>
                        <span className="text-sm text-gray-500">{formatarValor(pregao.valorEstimado)}</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Data de Início</p>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-[#0e7cb4] mr-1" />
                          <p className="text-sm">{formatarData(pregao.dataInicio)}</p>
                        </div>
                        <div className="flex items-center mt-1">
                          <Clock className="h-4 w-4 text-[#0e7cb4] mr-1" />
                          <p className="text-sm">{formatarHora(pregao.dataInicio)}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Data de Encerramento</p>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-[#0e7cb4] mr-1" />
                          <p className="text-sm">{formatarData(pregao.dataFim)}</p>
                        </div>
                        <div className="flex items-center mt-1">
                          <Clock className="h-4 w-4 text-[#0e7cb4] mr-1" />
                          <p className="text-sm">{formatarHora(pregao.dataFim)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {pregao.categoria === 'Florestal' && <TreePine className="h-5 w-5 text-green-600 mr-1" />}
                        {pregao.categoria === 'Energia Renovável' && <BadgeDollarSign className="h-5 w-5 text-amber-500 mr-1" />}
                        {pregao.categoria === 'Costeiro' && <CloudRain className="h-5 w-5 text-blue-500 mr-1" />}
                        {pregao.categoria === 'Agropecuário' && <Building className="h-5 w-5 text-orange-500 mr-1" />}
                        <span className="text-sm text-gray-600">{pregao.categoria}</span>
                      </div>
                      <Button 
                        className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white text-sm"
                        onClick={() => navegarParaPregao(pregao.id)}
                      >
                        Ver Detalhes
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          {/* Pregões em andamento */}
          <h2 className="text-xl font-semibold mb-4">Pregões em Andamento</h2>
          {pregoesEmAndamento.length > 0 ? (
            <div className="mb-8">
              {pregoesEmAndamento.map(pregao => (
                <Card key={pregao.id} className="mb-4 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row justify-between">
                      <div className="flex-grow">
                        <div className="flex items-center mb-2">
                          <Gavel className="w-5 h-5 text-amber-600 mr-2" />
                          <h3 className="text-lg font-semibold">{pregao.titulo} <span className="text-[#0e7cb4] ml-2">Código: {pregao.id}</span></h3>
                        </div>
                        <p className="text-gray-600 mb-3 text-sm line-clamp-2">{pregao.descricao}</p>
                        <div className="flex flex-wrap gap-4 mb-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <FileSpreadsheet className="w-4 h-4 mr-1 text-gray-500" />
                            <span>Código: {pregao.id}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <BarChart className="w-4 h-4 mr-1 text-gray-500" />
                            <span>Categoria: {pregao.categoria}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <DollarSign className="w-4 h-4 mr-1 text-gray-500" />
                            <span>Valor Estimado: {formatarValor(pregao.valorEstimado)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="flex items-center text-sm text-gray-600">
                            <span className="font-medium mr-1">Início:</span>
                            {formatarData(pregao.dataInicio)} às {formatarHora(pregao.dataInicio)}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <span className="font-medium mr-1">Término:</span>
                            {formatarData(pregao.dataFim)} às {formatarHora(pregao.dataFim)}
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs flex items-center ${obterStatusInfo(pregao.status).cor}`}>
                            {obterStatusInfo(pregao.status).icone}
                            {obterStatusInfo(pregao.status).texto}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 md:mt-0 md:ml-4 flex items-center md:items-end justify-end">
                        <Button
                          className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                          onClick={() => navegarParaPregao(pregao.id)}
                        >
                          Ver Pregão
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="mb-8">
              <CardContent className="p-6 text-center text-gray-500 py-12">
                Não há pregões em andamento no momento.
              </CardContent>
            </Card>
          )}
          
          {/* Pregões encerrados */}
          <h2 className="text-xl font-semibold mb-4">Pregões Encerrados</h2>
          {pregoesFechados.length > 0 ? (
            <div>
              {pregoesFechados.map(pregao => (
                <Card key={pregao.id} className="mb-4 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row justify-between">
                      <div className="flex-grow">
                        <div className="flex items-center mb-2">
                          <Gavel className="w-5 h-5 text-gray-600 mr-2" />
                          <h3 className="text-lg font-semibold text-gray-700">{pregao.titulo} <span className="text-[#0e7cb4] ml-2">Código: {pregao.id}</span></h3>
                        </div>
                        <p className="text-gray-600 mb-3 text-sm line-clamp-2">{pregao.descricao}</p>
                        <div className="flex flex-wrap gap-4 mb-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <FileSpreadsheet className="w-4 h-4 mr-1 text-gray-500" />
                            <span>Código: {pregao.id}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <BarChart className="w-4 h-4 mr-1 text-gray-500" />
                            <span>Categoria: {pregao.categoria}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <DollarSign className="w-4 h-4 mr-1 text-gray-500" />
                            <span>Valor Estimado: {formatarValor(pregao.valorEstimado)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="flex items-center text-sm text-gray-600">
                            <span className="font-medium mr-1">Início:</span>
                            {formatarData(pregao.dataInicio)} às {formatarHora(pregao.dataInicio)}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <span className="font-medium mr-1">Término:</span>
                            {formatarData(pregao.dataFim)} às {formatarHora(pregao.dataFim)}
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs flex items-center ${obterStatusInfo(pregao.status).cor}`}>
                            {obterStatusInfo(pregao.status).icone}
                            {obterStatusInfo(pregao.status).texto}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 md:mt-0 md:ml-4 flex items-center md:items-end justify-end">
                        <Button
                          className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                          onClick={() => abrirResultadosPregao(pregao)}
                        >
                          Ver Resultados
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-6 text-center text-gray-500 py-12">
                Não há pregões encerrados no momento.
              </CardContent>
            </Card>
          )}
          
          {/* Projetos Ofertantes */}
          <h2 className="text-xl font-semibold mb-4 mt-8">Projetos Ofertantes (geradores de crédito)</h2>
          {projetosOfertantes.length > 0 ? (
            <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {projetosOfertantes.map(projeto => (
                <Card key={projeto.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-3">
                      <div className="rounded-full p-3 bg-[#0e7cb4]/10 mr-3">
                        <TreePine className="h-5 w-5 text-[#0e7cb4]" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{projeto.nome}</h3>
                        <p className="text-sm text-gray-500">ID: {projeto.id}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 flex items-center">
                          <Leaf className="h-4 w-4 mr-1 text-green-600" />
                          Tipo:
                        </span>
                        <span className="text-sm font-medium">{projeto.tipo}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 flex items-center">
                          <MapPin className="h-4 w-4 mr-1 text-[#0e7cb4]" />
                          Região:
                        </span>
                        <span className="text-sm font-medium">{projeto.regiao}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 flex items-center">
                          <BadgeDollarSign className="h-4 w-4 mr-1 text-[#0e7cb4]" />
                          Créditos:
                        </span>
                        <span className="text-sm font-medium">{projeto.creditosGerados.toLocaleString('pt-BR')} KgCO2e</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className={`px-3 py-1 rounded-full text-xs ${
                        projeto.statusVerificacao === 'Verificado' ? 'bg-green-100 text-green-800' :
                        projeto.statusVerificacao === 'Em análise' ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {projeto.statusVerificacao}
                      </div>
                      
                      <Button
                        variant="outline" 
                        size="sm"
                        className="text-[#0e7cb4] border-[#0e7cb4] hover:bg-[#0e7cb4] hover:text-white"
                        onClick={() => navegarParaProjetoOfertante(projeto.id)}
                      >
                        Ver Pregão
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="mb-8">
              <CardContent className="p-6 text-center text-gray-500 py-8">
                Não há projetos ofertantes cadastrados no momento.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Modal de Resultados do Pregão */}
      {modalResultadosAberto && pregaoSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start">
                  <div className="rounded-full p-3 bg-[#0e7cb4]/10 mr-3">
                    <Gavel className="h-6 w-6 text-[#0e7cb4]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{pregaoSelecionado.titulo}</h2>
                    <p className="text-sm text-gray-500">Código: {pregaoSelecionado.id}</p>
                    <div className={`mt-1 px-3 py-1 rounded-full text-xs inline-flex items-center ${obterStatusInfo(pregaoSelecionado.status).cor}`}>
                      {obterStatusInfo(pregaoSelecionado.status).icone}
                      {obterStatusInfo(pregaoSelecionado.status).texto}
                    </div>
                  </div>
                </div>
                <button 
                  className="p-2 rounded-full hover:bg-gray-100"
                  onClick={() => setModalResultadosAberto(false)}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              {pregaoSelecionado.status === 'finalizado' ? (
                <div>
                  {resultadosPregoes[pregaoSelecionado.id] && (
                    <>
                      <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center mb-3">
                          <Award className="h-7 w-7 text-green-600 mr-3" />
                          <div>
                            <h3 className="text-lg font-semibold text-green-800">Empresa Vencedora</h3>
                            <p className="text-xl font-bold text-[#0e7cb4]">{resultadosPregoes[pregaoSelecionado.id].vencedor}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          <div className="p-3 bg-white rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500">Valor Final</p>
                            <p className="text-lg font-semibold text-gray-800">
                              {formatarValor(resultadosPregoes[pregaoSelecionado.id].valorFinal)}
                            </p>
                          </div>
                          
                          <div className="p-3 bg-white rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500">Créditos Adquiridos</p>
                            <p className="text-lg font-semibold text-gray-800">
                              {resultadosPregoes[pregaoSelecionado.id].quantidadeCreditos.toLocaleString('pt-BR')} KgCO2e
                            </p>
                          </div>
                          
                          <div className="p-3 bg-white rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500">Data de Finalização</p>
                            <p className="text-lg font-semibold text-gray-800">
                              {formatarData(resultadosPregoes[pregaoSelecionado.id].dataFinalizacao)} às {formatarHora(resultadosPregoes[pregaoSelecionado.id].dataFinalizacao)}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <h3 className="font-semibold text-lg flex items-center mb-4">
                        <TrendingUp className="h-5 w-5 text-[#0e7cb4] mr-2" />
                        Classificação Final
                      </h3>
                      
                      <div className="border rounded-lg overflow-hidden mb-6">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Posição
                              </th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Empresa
                              </th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Lance
                              </th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {resultadosPregoes[pregaoSelecionado.id].participantes
                              .sort((a, b) => a.posicao - b.posicao)
                              .map((participante, index) => (
                                <tr key={index} className={`${participante.status === 'vencedor' ? 'bg-green-50' : participante.status === 'desclassificado' ? 'bg-red-50' : ''} hover:bg-gray-50`}>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                    {participante.posicao}º
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-[#0e7cb4]/10">
                                        <User className="h-4 w-4 text-[#0e7cb4]" />
                                      </div>
                                      <div className="ml-3">
                                        <div className="text-sm font-medium text-gray-900">
                                          {participante.empresa}
                                          {participante.status === 'vencedor' && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                              <Award className="h-3 w-3 mr-1" />
                                              Vencedor
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                    {formatarValor(participante.lance)}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                                    {participante.status === 'vencedor' ? (
                                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                        <CircleCheck className="h-4 w-4 mr-1" />
                                        Aceito
                                      </span>
                                    ) : participante.status === 'desclassificado' ? (
                                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                        <X className="h-4 w-4 mr-1" />
                                        Desclassificado
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                        Participante
                                      </span>
                                    )}
                                  </td>
                                </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
                        <h3 className="font-semibold text-md mb-2">Observações:</h3>
                        <p className="text-sm text-gray-700">{resultadosPregoes[pregaoSelecionado.id].detalhes}</p>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="p-6 bg-red-50 rounded-lg border border-red-200 text-center">
                  <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Pregão Cancelado</h3>
                  <p className="text-gray-700 mb-4">
                    {resultadosPregoes[pregaoSelecionado.id]?.detalhes || 
                      'Este pregão foi cancelado. Não foram registrados resultados válidos.'}
                  </p>
                  
                  {resultadosPregoes[pregaoSelecionado.id]?.participantes && 
                    resultadosPregoes[pregaoSelecionado.id].participantes.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium text-gray-800 mb-2">Tentativas de Lance:</h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-white">
                            <tr>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Empresa
                              </th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Lance
                              </th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {resultadosPregoes[pregaoSelecionado.id].participantes.map((participante, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                  {participante.empresa}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                  {formatarValor(participante.lance)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                    <X className="h-4 w-4 mr-1" />
                                    Desclassificado
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-6 flex justify-end">
                <Button 
                  className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                  onClick={() => setModalResultadosAberto(false)}
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Login do Proprietário */}
      {modalLoginAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Acesso ao Proprietário</h2>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={fecharModalLogin}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {erroLogin && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{erroLogin}</p>
              </div>
            )}
            
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700">
                <strong>Modo de teste:</strong> Você pode usar qualquer email e senha para acessar a área do proprietário.
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="usuario" className="block text-sm font-medium text-gray-700 mb-1">
                  Email ou Usuário
                </Label>
                <Input
                  type="text"
                  id="usuario"
                  name="usuario"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4]"
                  value={credenciais.usuario}
                  onChange={handleInputChange}
                />
              </div>
              
              <div>
                <Label htmlFor="senha" className="block text-sm font-medium text-gray-700 mb-1">
                  Senha
                </Label>
                <Input
                  type="password"
                  id="senha"
                  name="senha"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4]"
                  value={credenciais.senha}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="pt-2">
                <Button 
                  className="w-full bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                  onClick={realizarLogin}
                >
                  Entrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Rodapé */}
      <Footer />
    </div>
  );
};

export default PregaoDashboard; 